import { Category } from '@modules/common/types';
import { FeedbackLabelsWrapper } from '@modules/feedback/components/FeedbackCard/FeedbackLabelList/FeedbackLabelsWrapper';
import { useIsFeedbackOnFeedbackEnabled } from '@modules/feedback/hooks';
import { SetSelectedLabels } from '@modules/feedback/types';
import { Box, Stack, Tooltip } from '@mui/material';
import { find, take, takeRight } from 'lodash';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel as FeedbackLabelInterface,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';
import { FeedbackLabelV2 } from '../FeedbackLabelV2';
import { FeedbackTruncatedLabels } from './FeedbackTruncatedLabels';

export interface FeedbackLabelListProps {
  feedback: Feedback;
  category: Category;
  labels: FeedbackLabelInterface[];
  isEditPredictionsMode: boolean;
  selectedLabels: FeedbackLabelInterface[];
  shouldTruncateLabels?: boolean;
  truncationLength?: number;
  setSelectedLabels?: SetSelectedLabels;
  isLabelsSelectionDisabled?: boolean;
  disabledLabelIDs?: string[];
  onFeedbackCardLabelClick?: (selectedLabels: FeedbackLabelInterface[]) => void;
  disableLabelSelection?: boolean;
  shouldSortBySelection?: boolean;
}

export const FeedbackLabelList = ({
  feedback,
  category,
  labels,
  isEditPredictionsMode,
  selectedLabels,
  setSelectedLabels,
  shouldTruncateLabels = false,
  truncationLength,
  isLabelsSelectionDisabled = false,
  disabledLabelIDs,
  onFeedbackCardLabelClick,
  disableLabelSelection,
}: FeedbackLabelListProps) => {
  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();
  // Since, truncationLength can be 0 as well;
  // explicitly checking for undefined value of truncationLength
  const isTruncatedLabelsVisible =
    shouldTruncateLabels && truncationLength !== undefined;

  // We show only the first `truncationLength` labels on the feedback listing page.
  // If truncationLength is 0, we show only one label in that case. This can happen
  // if screen width is too small to accommodate even a single label.
  const filteredLabels = isTruncatedLabelsVisible
    ? take(labels, truncationLength === 0 ? 1 : truncationLength)
    : labels;

  const remainingLabels = isTruncatedLabelsVisible
    ? takeRight(labels, (labels?.length || 0) - truncationLength)
    : labels;

  const shouldRenderRemainingLabelText =
    isTruncatedLabelsVisible && remainingLabels.length !== 0;

  return (
    <Tooltip
      title={
        isLabelsSelectionDisabled && !shouldTruncateLabels
          ? 'Switch to Conversation window to view the prediction highlights'
          : ''
      }
      placement="top"
      arrow
    >
      <Stack flexWrap="wrap" rowGap={1} columnGap={1} width="100%">
        <Stack
          direction="row"
          {...(isFeedbackOnFeedbackEnabled ? {} : { flexWrap: 'wrap' })}
          rowGap={1}
          columnGap={1}
          alignItems="center"
          width="100%"
          display={
            isFeedbackOnFeedbackEnabled && !shouldTruncateLabels
              ? 'inline-block'
              : 'flex'
          }
        >
          <FeedbackLabelsWrapper shouldTruncateLabels={shouldTruncateLabels}>
            {filteredLabels?.map((label) => {
              const isSelected = !!find(selectedLabels, {
                id: label.id,
              })?.id;

              const isClickable =
                feedback.content.type === FeedbackContentType.Audio ||
                (!shouldTruncateLabels &&
                  label.type === FeedbackLabelType.Entity);

              return (
                <Box
                  sx={{
                    pointerEvents: isLabelsSelectionDisabled ? 'none' : 'auto',
                  }}
                  key={label.id}
                >
                  <FeedbackLabelV2
                    label={label}
                    category={category}
                    setSelectedLabels={setSelectedLabels}
                    selectedLabels={selectedLabels}
                    isSelected={isSelected}
                    isDisabled={
                      disableLabelSelection ||
                      disabledLabelIDs?.includes(label.id)
                    }
                    onFeedbackCardLabelClick={onFeedbackCardLabelClick}
                    feedback={feedback}
                    isDeletable={isEditPredictionsMode}
                    size={shouldTruncateLabels ? 'small' : 'large'}
                    isEditable={!shouldTruncateLabels}
                    isClickable={isClickable}
                    shouldHideVisibilityIcon={shouldTruncateLabels}
                  />
                </Box>
              );
            })}
          </FeedbackLabelsWrapper>
          {shouldRenderRemainingLabelText && (
            <FeedbackTruncatedLabels
              totalLabelsLength={labels.length}
              truncationLength={truncationLength}
              category={category}
              setSelectedLabels={setSelectedLabels}
              selectedLabels={selectedLabels}
              onFeedbackCardLabelClick={onFeedbackCardLabelClick}
              feedback={feedback}
              remainingLabels={remainingLabels}
              disableLabelSelection={disableLabelSelection}
              disabledLabelIDs={disabledLabelIDs}
              isLabelsSelectionDisabled={isLabelsSelectionDisabled}
            />
          )}
        </Stack>
      </Stack>
    </Tooltip>
  );
};
